// How each of `GET /docs`'s four extraction states is spoken to a reader.
//
// Frame 14 draws only three pills — `published` (nominal), `in review`
// (flagged) and `draft` (neutral). `unavailable` has no drawn pill because
// the degraded path was designed after the frame, and it MUST NOT borrow
// `draft`'s: "draft" says the doc is waiting for someone to extract it,
// while "unavailable" says nobody can, because no language-model key is
// configured. A reader who reads the first when the second is true goes
// looking for a review queue that cannot exist.

import type { BadgeTone } from "../../ui";
import type { DocStatus } from "./api";

export interface StatusPresentation {
  label: string;
  tone: BadgeTone;
  /** Screen-reader / tooltip sentence. The pill alone is too terse. */
  detail: string;
}

const PRESENTATION: Record<DocStatus, StatusPresentation> = {
  published: {
    label: "published",
    tone: "nominal",
    detail: "Glossary published and live to readers.",
  },
  extracted: {
    label: "in review",
    tone: "flagged",
    detail: "Terms proposed, waiting for a reviewer.",
  },
  not_extracted: {
    label: "draft",
    tone: "neutral",
    detail: "Parsed. Extraction has not run yet.",
  },
  unavailable: {
    label: "unavailable",
    tone: "danger",
    detail:
      "Extraction status cannot be determined — no language-model key is configured.",
  },
};

export function presentStatus(status: DocStatus): StatusPresentation {
  return PRESENTATION[status];
}

/** The degraded path is a page-level fact, not a per-row one. */
export function hasUnavailable(statuses: DocStatus[]): boolean {
  return statuses.some((status) => status === "unavailable");
}
